import React, { useState, useCallback, useRef, useEffect } from "react";
import { useHistory } from 'react-router-dom';
import { connect } from "react-redux";
import { Button } from 'devextreme-react/button';
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import { LoadPanel } from 'devextreme-react/load-panel';
import Toolbar, { Item } from "devextreme-react/toolbar";
import PrimaryTab from "./tabs/PrimaryTab";
import DetailsTab from "./tabs/DetailsTab";
import UserDefinedTab from "./tabs/UserDefinedTab";
import axios from "axios";
import data from "../../../../assets/apiRoutes.json";
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

// User-defined field mapping between form state (UPPERCASE) and DTO (PascalCase).
const USER_DEFINED_FIELDS = [];
['CODE', 'DATE', 'QUANTITY', 'REFERENCE'].forEach((kind) => {
    for (let i = 1; i <= 5; i++) {
        const suffix = kind.charAt(0) + kind.slice(1).toLowerCase(); // Code / Date / Quantity / Reference
        USER_DEFINED_FIELDS.push({ form: `USERDEFINED${kind}${i}`, dto: `UserDefined${suffix}${i}` });
    }
});

function Form(props) {
    const history = useHistory();
    const formRefs = useRef({});
    const initialFormFields = {
        ROWID: EMPTY_GUID,
        RECORD: '',
        CODE: '',
        DESCRIPTION1: '',
        DESCRIPTION2: '',
        STATUS: 'A',
        COMMENT: '',
    };
    USER_DEFINED_FIELDS.forEach((f) => { initialFormFields[f.form] = null; });

    const [formFields, setformFields] = useState(initialFormFields);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const accessToken = props?.oidc?.user?.access_token;

    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, []);

    const position = { of: '#accordion' };

    const handleFieldChange = useCallback((name, value) => {
        setformFields(prevFields => ({
            ...prevFields,
            [name]: value
        }));
    }, []);

    const mapResponseToFields = useCallback((item) => {
        const fields = {
            ROWID: item.RowId || EMPTY_GUID,
            RECORD: item.RecordNumber || '',
            CODE: item.Code || '',
            DESCRIPTION1: item.Description1 || '',
            DESCRIPTION2: item.Description2 || '',
            STATUS: item.Status || 'A',
            COMMENT: item.Comment || '',
        };
        USER_DEFINED_FIELDS.forEach((f) => { fields[f.form] = item[f.dto] ?? null; });
        return fields;
    }, []);

    const buildPayload = useCallback(() => {
        const payload = {
            RowId: formFields.ROWID && formFields.ROWID !== EMPTY_GUID ? formFields.ROWID : EMPTY_GUID,
            KeySequence: 0,
            Code: formFields.CODE || "",
            Description1: formFields.DESCRIPTION1 || "",
            Description2: formFields.DESCRIPTION2 || "",
            Status: formFields.STATUS || "A",
            Comment: formFields.COMMENT || "",
        };
        USER_DEFINED_FIELDS.forEach((f) => {
            const value = formFields[f.form];
            payload[f.dto] = value === '' ? null : (value ?? null);
        });
        return payload;
    }, [formFields]);

    const loadExceptionHeaderById = useCallback(async (rowId) => {
        if (!rowId || rowId === EMPTY_GUID) {
            return;
        }

        try {
            setLoadPanelVisible(true);
            const url = `${window._env_.REACT_APP_APIS}/${data.Exceptions.GetExceptionHeader}?rowid=${rowId}`;
            const response = await axios.get(url, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`
                }
            });

            if (response.data) {
                setformFields(mapResponseToFields(response.data));
            }
        } catch (error) {
            console.error('Error loading Exception Header:', error);
            notify('Failed to load Warehouse Exception data.', 'error', 3000);
        } finally {
            setLoadPanelVisible(false);
        }
    }, [accessToken, mapResponseToFields]);

    useEffect(() => {
        const rowId = props.id;
        if (rowId && rowId !== EMPTY_GUID) {
            loadExceptionHeaderById(rowId);
        }
    }, [props.id, loadExceptionHeaderById]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setLoadPanelVisible(true);

        try {
            const payload = buildPayload();

            const isUpdate = formFields.ROWID && formFields.ROWID !== EMPTY_GUID;
            const endpoint = isUpdate ? data.Exceptions.UpdateExceptionHeader : data.Exceptions.InsertExceptionHeader;
            const method = isUpdate ? 'put' : 'post';

            const url = `${window._env_.REACT_APP_APIS}/${endpoint}`;
            const resp = await axios[method](url, payload, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                }
            });

            const saved = resp?.data;
            if (saved) {
                setformFields(mapResponseToFields(saved));
            }

            notify("Warehouse Exception saved successfully.", "success", 3000);
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                (typeof err?.response?.data === "string" ? err.response.data : null) ||
                err?.message ||
                "Failed to save Warehouse Exception.";

            notify(message, "error", 5000);
        } finally {
            hideLoadPanel();
        }
    };

    const DeleteClick = () => {
        if (!formFields.ROWID || formFields.ROWID === EMPTY_GUID) {
            notify("No record selected for deletion.", "warning", 3000);
            return;
        }

        let result = confirm("<i>Are you sure you want to delete this Warehouse Exception?</i>", "Delete");
        result.then(async (dialogResult) => {
            if (dialogResult) {
                setLoadPanelVisible(true);
                try {
                    const url = `${window._env_.REACT_APP_APIS}/${data.Exceptions.DeleteExceptionHeader}`;
                    const response = await axios.post(url, formFields.ROWID, {
                        headers: {
                            access_token: accessToken,
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json'
                        }
                    });

                    if (response.data && response.data.success) {
                        notify("Warehouse Exception deleted successfully.", "success", 3000);
                        setformFields(initialFormFields);
                        setTimeout(() => {
                            history.push("/wm/codes/exceptions");
                        }, 1000);
                    } else {
                        notify(response.data?.message || "Failed to delete Warehouse Exception.", "error", 5000);
                    }
                } catch (error) {
                    const message = error?.response?.data?.message || error?.message || "Failed to delete Warehouse Exception.";
                    notify(message, "error", 5000);
                } finally {
                    hideLoadPanel();
                }
            }
        });
    };

    const addNewRecordClick = () => {
        setformFields(initialFormFields);
    };

    const handleReset = () => {
        const rowId = props.id;
        if (rowId && rowId !== EMPTY_GUID) {
            loadExceptionHeaderById(rowId);
        } else {
            setformFields(initialFormFields);
        }
    };

    const goBack = () => {
        history.push("/wm/codes/exceptions");
    };

    return (
        <div>
            <div className="row dataEntryMainPage">
                <div className="col-md-12 main-page">
                    <div id="accordion">
                        <LoadPanel
                            shadingColor="rgba(0,0,0,0.4)"
                            position={position}
                            onHiding={hideLoadPanel}
                            visible={loadPanelVisible}
                            showIndicator={true}
                            shading={true}
                            showPane={true}
                            hideOnOutsideClick={false}
                        />
                        <form onSubmit={(e) => handleFormSubmit(e)}>
                            <Toolbar className="header">
                                <Item location="before">
                                    <div className="title">
                                        <h6
                                            style={{
                                                color: window._env_.Color2,
                                                marginLeft: '12px'
                                            }}
                                        >
                                            {formFields.CODE ? `Warehouse Exception - ${formFields.CODE}` : 'Warehouse Exception'}
                                        </h6>
                                    </div>
                                </Item>
                                <Item location="after">
                                    <Button
                                        text="Save"
                                        useSubmitBehavior={true}
                                        type="default"
                                        className="accordianButton"
                                    />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={addNewRecordClick} text="New" type="default" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={DeleteClick} text="Delete" disabled={formFields.ROWID === EMPTY_GUID} type="default" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={handleReset} text="Reset" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={goBack} text="Back" />
                                </Item>
                            </Toolbar>
                            <TabPanel deferRendering={props.id ? true : false} showNavButtons={true} height={"auto"}>
                                <TabItem title="Primary">
                                    <PrimaryTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                                <TabItem title="Details">
                                    <DetailsTab
                                        headerId={formFields.ROWID}
                                    />
                                </TabItem>
                                <TabItem title="User Defined">
                                    <UserDefinedTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                            </TabPanel>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(Form);
